/*
 * � 2025, NTT Data Group. All rights reserved. See LICENSE.txt file in the project root for third party licenses info.
 */

import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { ScenarioFull, PollingStatus } from "../models/http/what-if/scenario/scenario-full.model";
import { WhatIfScenarioHttpService } from "./http/what-if-scenario-http.service";

@Injectable({
	providedIn: "root"
})
export class WhatIfScenarioService {
	constructor(private _whatIfScenarioHttpService: WhatIfScenarioHttpService) {}

	/**
	 * Creates a new scenario for the specified application.
	 *
	 * @param appId - The ID of the application for which the scenario is being created.
	 * @param startingScenario - The starting scenario number.
	 * @param tenantId - The ID of the tenant.
	 * @param name - (Optional) The name of the new scenario.
	 * @returns An Observable that emits the ID of the newly created scenario.
	 */
	public createNewScenario(
		appId: string,
		startingScenario: number,
		tenantId: string,
		name?: string
	): Observable<{ id: string }> {
		return this._whatIfScenarioHttpService.createNewScenario(
			appId,
			startingScenario,
			tenantId,
			name
		);
	}

	/**
	 * Retrieves detailed information about a specific scenario.
	 *
	 * @param appId - The application ID associated with the scenario.
	 * @param scenarioId - The unique identifier of the scenario.
	 * @param tenantId - The tenant ID to which the scenario belongs.
	 * @param time - (Optional) A specific time to retrieve the scenario information for.
	 * @returns An Observable that emits the full scenario information.
	 */
	public getScenarioInfo(
		appId: string,
		scenarioId: number,
		tenantId: string,
		time?: string
	): Observable<ScenarioFull> {
		return this._whatIfScenarioHttpService.getScenarioInfo(appId, scenarioId, tenantId, time);
	}

	/**
	 * Deletes a scenario based on the provided application ID, scenario ID, and tenant ID.
	 *
	 * @param appId - The ID of the application.
	 * @param scenarioId - The ID of the scenario to be deleted.
	 * @param tenantId - The ID of the tenant.
	 * @returns An Observable that emits an object containing a message upon successful deletion.
	 */
	public deleteScenario(appId: string, scenarioId: number, tenantId: string): Observable<string> {
		return this._whatIfScenarioHttpService.deleteScenario(appId, scenarioId, tenantId);
	}

	/**
	 * Retrieves the simulation status of a specific scenario for a given application and tenant.
	 *
	 * @param appId - The ID of the application.
	 * @param scenarioId - The ID of the scenario.
	 * @param tenantId - The ID of the tenant.
	 * @returns An Observable that emits an object containing the simulation status.
	 */
	public getScenarioSimulationStatus(
		appId: string,
		scenarioId: number,
		tenantId: string
	): Observable<{ status: PollingStatus }> {
		return this._whatIfScenarioHttpService.getScenarioSimulationStatus(
			appId,
			scenarioId,
			tenantId
		);
	}
}
